/**
 * THE ONE CHALLANS REGISTER (CTS1, DeliveryChallans.jsx — the owner's 18 Sep
 * instruction: learn from the old pages). Delivery challans and return
 * challans live in ONE register, told apart by a filter, never by a page.
 * A row opens IN PLACE — the full challan with its papers, its money and its
 * actions — so the person checking the day's work never loses the register
 * to act on one row. Approving is possible here too, exactly as it was on
 * the old register; the deeper journey (departure, the gate) opens from the
 * challan itself.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Count, Loading, Money, Pill, Register, Seg, Select, Serial, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { challanStanding, shipmentStanding, actionWords, STANDING_WORDS, type StandingWord } from '../standing.js';
import { count, date, today, words, when, instant } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('challans')!.messages;

type Dir = '' | 'dispatch' | 'return';
/**
 * WHERE IT STANDS, AS THE ROWS SAY IT (audit L4). The words come from the
 * view's own published vocabulary, so the filter cannot drift from what the
 * register prints in its "Where it stands" column.
 */
type Standing = '' | StandingWord;

/**
 * THE REGISTER OPENS ON THIS MONTH (audit L3). A yard register whose first
 * page is the oldest challan in the company is a register nobody works from;
 * the month is a filter like any other, shown in the From box and cleared
 * with one click, never a silent cap (DEF-141's lesson).
 */
function firstOfThisMonth(): string {
  return `${today().slice(0, 7)}-01`;
}

export function Challans({ id, session }: { id: string | null; session: Session }) {
  return (
    <Page title="Challans" sub="Out and in, one register — filter, review, act. The paper never leaves the page.">
      <ChallansPanel id={id} session={session} />
    </Page>
  );
}

/** 10 Oct — THE PAPERS REGISTER: every paper issued, by number, customer, kind and date; open from here. */
/**
 * 14 Oct — THE CUSTOMER PAPERS, kept apart from ours on the owner's instruction.
 *
 * A delivery challan says our cylinders went out on rental and starts the holding clock.
 * A Customer Fill Receipt and a Customer Fill Dispatch say somebody else's property came
 * in for work and went home again, and carry no rent at any stage. Mixing them in one
 * register is how a person comes to read the second as the first.
 */
export function CustomerPapersPanel({ customerId }: { customerId?: number }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [q, kind]);
  const asked = useAsk(() => api.view('documents.list', {
    ...(q.trim() ? { q: q.trim() } : {}),
    kind: (kind || 'inward_receipt') as 'inward_receipt',
    ...(customerId ? { customerId } : {}), page, pageSize: 50 }), [q, kind, customerId, page], MESSAGES);
  const both = useAsk(() => api.view('documents.list', {
    ...(q.trim() ? { q: q.trim() } : {}),
    kind: 'customer_fill_dispatch' as 'inward_receipt',
    ...(customerId ? { customerId } : {}), page: 1, pageSize: 50 }), [q, customerId], MESSAGES);
  const rows = kind === ''
    ? [...(asked.data?.rows ?? []), ...(both.data?.rows ?? [])].sort((a, b) => (a.issuedAt < b.issuedAt ? 1 : -1))
    : (asked.data?.rows ?? []);
  return (
    <>
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="cpaper-q">Paper number or customer</label>
          <input id="cpaper-q" type="text" value={q} placeholder="KIG/CFD/26-27/0001 · a customer" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Select name="cpaper-kind" label="Which" value={kind} onChange={setKind} placeholder="Both"
                options={[{ value: 'inward_receipt', label: 'Customer Fill Receipt (CFR) — arrived' },
                          { value: 'customer_fill_dispatch', label: 'Customer Fill Dispatch (CFD) — went home' }]} />
      </div>
      <Card title="Customer cylinder papers"
            sub="their cylinders, in for filling and back out again — job work, never rental, no holding charge at any stage">
        {asked.loading && !asked.data ? <Loading what="the papers" />
          : asked.error ? <Banner kind="error" title={asked.error.title} />
          : rows.length === 0
            ? <p className="muted">No customer cylinder papers yet. One is issued when a customer's cylinders are received for filling, and another when they go home.</p>
            : (
              <Register columns={[
                { key: 'number', head: 'Paper', cell: (r: { documentId: number; number: string | null }) =>
                    <a href={api.documentUrl(r.documentId)} target="_blank" rel="noreferrer"><Serial>{r.number ?? `#${r.documentId}`}</Serial></a> },
                { key: 'kind', head: 'Which', cell: (r: { kind: string }) =>
                    r.kind === 'customer_fill_dispatch' ? 'Went home (CFD)' : 'Arrived (CFR)' },
                { key: 'issued', head: 'Issued', cell: (r: { issuedAt: string }) => date(r.issuedAt.slice(0, 10)) },
              ]} rows={rows} empty="No customer cylinder papers match that." />
            )}
      </Card>
    </>
  );
}

export function PapersPanel({ customerId }: { customerId?: number }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [q, kind, from, to]);
  const asked = useAsk(() => api.view('documents.list', {
    ...(q.trim() ? { q: q.trim() } : {}), ...(kind ? { kind: kind as 'dispatch_challan' } : {}),
    ...(customerId ? { customerId } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}), page, pageSize: 50 }), [q, kind, customerId, from, to, page], MESSAGES);
  return (
    <>
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="paper-q">Paper number, challan number or customer</label>
          <input id="paper-q" type="text" value={q} placeholder="KIG/DC/26-27/0101 · PI/26-27/00121 · a customer" onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Select name="paper-kind" label="Kind" value={kind} onChange={setKind} placeholder="Every kind"
                options={[{ value: 'dispatch_challan', label: 'Delivery challan' }, { value: 'delivery_memo', label: 'Delivery memo' }, { value: 'dispatch_slip', label: 'Dispatch slip' },
                          { value: 'return_challan', label: 'Receipt challan' }, { value: 'monthly_bill', label: 'Monthly bill' }, { value: 'payment_receipt', label: 'Money receipt' }, { value: 'inward_receipt', label: 'Inward receipt' }, { value: 'quotation', label: 'Quotation (offer)' }]} />
        <Text name="paper-from" label="From" type="date" value={from} onChange={setFrom} />
        <Text name="paper-to" label="To" type="date" value={to} onChange={setTo} />
      </div>
      <Card title="Papers" sub="every document this company has issued — open it, or go to what it belongs to">
        {asked.loading && !asked.data ? <Loading what="the papers" />
          : asked.error ? <Banner kind="error" title={asked.error.title} />
          : (<>
              <Register columns={[
                  { key: 'number', head: 'Paper', cell: (r: { documentId: number; number: string | null; kind: string }) => <a href={api.documentUrl(r.documentId)} target="_blank" rel="noreferrer"><Serial>{r.number ?? `#${r.documentId}`}</Serial></a> },
                  { key: 'kind', head: 'Kind', cell: (r: { kind: string }) => words(r.kind) },
                  { key: 'issued', head: 'Issued', cell: (r: { issuedAt: string }) => instant(r.issuedAt) },
                  { key: 'customer', head: 'Customer', cell: (r: { customerName: string | null; customerId: number | null }) => r.customerId ? <a href={`#/customers/${r.customerId}`}>{r.customerName}</a> : <span className="muted">—</span> },
                  { key: 'belongs', head: 'Belongs to', cell: (r: { challanId: number | null; challanNumber: string | null; billId: number | null; paymentId: number | null }) =>
                      r.challanId ? <a href={`#/dispatch/${r.challanId}`}>challan {r.challanNumber ?? `#${r.challanId}`}</a> : r.billId ? `bill #${r.billId}` : r.paymentId ? `receipt #${r.paymentId}` : '—' },
                  { key: 'open', head: '', cell: (r: { documentId: number }) => <a className="btn small" href={api.documentUrl(r.documentId)} target="_blank" rel="noreferrer">Open PDF</a> },
                ]} rows={asked.data!.rows} empty="No paper matches that."
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </>
  );
}

/** 10 Oct — the register without its page frame, so Movements can carry it as its Register tab. */
export function ChallansPanel({ id, session }: { id: string | null; session: Session }) {
  const [dir, setDir] = useState<Dir>('');
  const [standing, setStanding] = useState<Standing>('');
  const [customerId, setCustomerId] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(firstOfThisMonth());
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  // the route carries the expanded row so a challan link lands open (lesson: props sync via effect)
  const [openId, setOpenId] = useState<number | null>(id ? Number(id) : null);
  useEffect(() => { setOpenId(id ? Number(id) : null); }, [id]);
  useEffect(() => { setPage(1); }, [dir, standing, customerId, q, from, to]);

  const asked = useAsk(() => api.view('challans.list', {
    ...(dir ? { type: dir } : {}), ...(standing ? { standing } : {}),
    ...(customerId ? { customerId: Number(customerId) } : {}), ...(q.trim() ? { q: q.trim() } : {}),
    ...(from ? { from } : {}), ...(to ? { to } : {}),
    page, pageSize: 50,
  }), [dir, standing, customerId, q, from, to, page], MESSAGES);

  const filters = (dir ? 1 : 0) + (standing ? 1 : 0) + (customerId ? 1 : 0) + (q.trim() ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0);

  type Row = {
    challanId: number; number: string | null; legacyNumber: string | null; type: string; customerName: string; siteName: string;
    eventDate: string; status: string; journey: string | null; lineCount: number; vehicle: string | null; reverseReason: string | null;
  };
  const columns: readonly Column<Row>[] = [
    { key: 'number', head: 'Challan', cell: (r) => <>
        {r.number ? <Serial>{r.number}</Serial> : <span className="muted">not issued</span>}
        {r.legacyNumber ? <div className="muted">old: {r.legacyNumber}</div> : null}
      </> },
    { key: 'dir', head: 'Way', cell: (r) => <Pill tone={r.type === 'dispatch' ? 'good' : 'empty'}>{r.type === 'dispatch' ? '↑ out' : '↓ in'}</Pill> },
    { key: 'date', head: 'Date', cell: (r) => date(r.eventDate) },
    { key: 'customer', head: 'Party', cell: (r) => <><strong>{r.customerName}</strong><div className="muted">{r.siteName}</div></> },
    { key: 'cyl', head: 'Cylinders', right: true, cell: (r) => <Count n={r.lineCount} /> },
    { key: 'vehicle', head: 'Truck', cell: (r) => r.vehicle ?? <span className="muted">—</span> },
    { key: 'status', head: 'Where it stands', cell: (r) => <>
        {/* R20261005-07 — THE STAGE AND THE NEXT ACTION TOGETHER. A register that says only
            where a thing stands leaves the reader to work out what to do with it, which is
            what made an out-for-delivery load hard to navigate out of. */}
        {(() => {
          const s = shipmentStanding(r.type, r.status, r.journey);
          return <>
            <Pill tone={s.tone}>{s.word}</Pill>
            {s.next ? <div className="muted">{s.next}</div> : null}
          </>;
        })()}
        {r.reverseReason ? <div className="muted" style={{ color: 'var(--danger)' }}>{r.reverseReason}</div> : null}
      </> },
  ];

  return (
    <>
      <div className="toolbar" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <span><span className="seg-label">Way</span>
          <Seg name="direction" value={dir} onChange={(v) => setDir(v as Dir)}
               options={[{ value: '', label: 'All' }, { value: 'dispatch', label: '↑ Out' }, { value: 'return', label: '↓ In' }]} /></span>
        {filters ? <Button small onClick={() => { setDir(''); setStanding(''); setCustomerId(''); setQ(''); setFrom(''); setTo(''); }}>Clear {count(filters)} filter{filters === 1 ? '' : 's'}</Button> : null}
      </div>
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="challan-q">Search</label>
          <input id="challan-q" type="text" value={q} placeholder="Challan no · old number · party · truck"
                 onChange={(e: { target: { value: string } }) => setQ(e.target.value)} />
        </div>
        <Select name="standing" label="Where it stands" value={standing} onChange={(v) => setStanding(v as Standing)} placeholder="All"
                options={STANDING_WORDS.map((w) => ({ value: w.value, label: w.label }))} />
        <CustomerPickerById name="party" label="Party" customerId={customerId}
                            onPick={(v) => setCustomerId(v)} placeholder="All parties — search to narrow" />
        <Text name="from" label="From" type="date" value={from} onChange={setFrom} />
        <Text name="to" label="To" type="date" value={to} onChange={setTo} />
      </div>

      {openId !== null ? <OneChallanCard challanId={openId} session={session} onClose={() => go('#/challans')}
                                          onChanged={() => asked.reload()} /> : null}

      <Card>
        {asked.loading && !asked.data ? <Loading what="the register" />
          : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No challan matches that."
                        onOpen={(r) => go(`#/challans/${r.challanId}`)}
                        totals={<span>Showing <b>{count(asked.data!.rows.length)}</b> of <b>{count(asked.data!.page.total)}</b> · by event date</span>} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </>
  );
}

/**
 * THE ROW, OPENED IN PLACE. Everything the old register's expanded row
 * carried: the lines, the pending charges, the memo, the papers, the audit,
 * and the actions this person may take — approve, reject, reverse, or step
 * into the journey where it continues.
 */
function OneChallanCard({ challanId, session, onClose, onChanged }: {
  challanId: number; session: Session; onClose: () => void; onChanged: () => void;
}) {
  const asked = useAsk(() => api.view('challans.get', { challanId }), [challanId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [reverseReason, setReverseReason] = useState('');

  if (asked.loading && !asked.data) return <Card><Loading what="the challan" /></Card>;
  if (asked.error) return <Card><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;
  const c = d.challan;

  async function approve() {
    const ok = await runner.run((retryKey) => api.command('challan.approve', { challanId }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { asked.reload(); onChanged(); }
  }
  async function reject() {
    const ok = await runner.run(() => api.command('challan.reject', { challanId, reason: reason.trim() }));
    if (ok) { setRejecting(false); setReason(''); asked.reload(); onChanged(); }
  }
  async function reverse() {
    const ok = await runner.run((retryKey) => api.command('challan.reverse',
      { challanId, reason: reverseReason.trim() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReversing(false); setReverseReason(''); asked.reload(); onChanged(); }
  }

  return (
    <Card title={`${c.type === 'dispatch' ? 'Delivery challan' : 'Return challan'} ${c.number ?? `#${c.challanId}`}`}
          sub={<>Customer: <a href={`#/customers/${c.customerId}?tab=challans`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/customers/${c.customerId}?tab=challans`); }}>{c.customerName}</a> · {c.siteName} · {date(c.eventDate)}{c.legacyNumber ? ` · old register ${c.legacyNumber}` : ''}</>}
          actions={<>
            {c.status === 'pending' && can(session, 'challan.approve') ? (
              <>
                <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>{runner.busy ? 'Approving…' : 'Approve'}</Button>
                <Button kind="danger" onClick={() => setRejecting((v) => !v)}>{rejecting ? 'Keep it' : 'Reject'}</Button>
              </>
            ) : null}
            {c.status === 'approved' && can(session, 'challan.reverse')
              ? <Button kind="danger" onClick={() => setReversing((v) => !v)}>{reversing ? 'Let it stand' : 'Reverse'}</Button> : null}
            <Button onClick={onClose}>Close</Button>
          </>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {c.status === 'reversed' ? (
        <Banner kind="warn" title="This challan was REVERSED — it should never have been entered."
                {...(c.reverseReason ? { detail: `${c.reverseReason} — the papers below stay exactly as issued; the reversal is its own recorded action.` } : {})} />
      ) : null}
      {c.status === 'rejected' && c.rejectReason ? <Banner kind="warn" title="Rejected." detail={c.rejectReason} /> : null}

      {rejecting ? (
        <div style={{ marginBottom: 12 }}>
          <TextArea name="row-reject-reason" label="Why it is rejected" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void reject(); }} disabled={runner.busy || !reason.trim()}>Reject the challan</Button>
        </div>
      ) : null}
      {reversing ? (
        <div style={{ marginBottom: 12 }}>
          <TextArea name="row-reverse-reason" label="Why this challan should never have been entered" value={reverseReason} onChange={setReverseReason} />
          <Button kind="danger" onClick={() => { void reverse(); }} disabled={runner.busy || !reverseReason.trim()}>
            {runner.busy ? 'Reversing…' : 'Reverse it — custody and money come back'}
          </Button>
        </div>
      ) : null}

      <div className="detail-grid" style={{ marginBottom: 12 }}>
        <div><div className="k">Where it stands</div><div className="v">{(() => { const s = challanStanding(c.type, c.status, c.journey); return <Pill tone={s.tone}>{s.word}</Pill>; })()}</div></div>
        <div><div className="k">Cylinders</div><div className="v">{count(c.lineCount)}</div></div>
        <div><div className="k">Truck</div><div className="v">{c.vehicle ?? '—'}{c.driver ? ` · ${c.driver}` : ''}</div></div>
        <div><div className="k">The journey</div><div className="v">
          <a href={`#/${c.type === 'dispatch' ? 'dispatch' : 'returns'}/${c.challanId}`}>open in full</a>
          {c.status === 'approved' && c.type === 'dispatch' && c.journey !== 'departed' && can(session, 'dispatch.depart')
            ? <> · <a href={`#/departure/${c.challanId}`}>record departure</a></> : null}
          {c.journey === 'departed' && can(session, 'delivery.confirm')
            ? <> · <a href={`#/delivery/${c.challanId}`}>confirm delivery</a></> : null}
        </div></div>
        <div><div className="k">Account</div><div className="v"><a href={`#/account/${c.customerId}`}>holdings and money</a></div></div>
      </div>

      <div className="grid two">
        <div>
          <h4 style={{ margin: '0 0 6px' }}>What is on it</h4>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {d.lines.map((l: { lineId: number; internalId: string; serialNo: string; gasService: string; deliveryOutcome: string | null }) => (
              <li key={l.lineId}><Serial>{l.internalId}</Serial> <span className="muted">· {l.serialNo} · {words(l.gasService)}</span>
                {l.deliveryOutcome ? <> <Pill>{l.deliveryOutcome}</Pill></> : null}</li>
            ))}
          </ul>
          {d.pendingCharges.length ? (
            <>
              <h4 style={{ margin: '12px 0 6px' }}>Charges added while checking</h4>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {d.pendingCharges.map((ch: { chargeId: number; description: string; chargeType: string; amountPaise: number }) => (
                  <li key={ch.chargeId}>{ch.description} <span className="muted">· {words(ch.chargeType)}</span> — <Money paise={ch.amountPaise} plain /></li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
        <div>
          <h4 style={{ margin: '0 0 6px' }}>Papers</h4>
          {d.documents.length === 0 ? <p className="muted">No document has been issued yet.</p> : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {d.documents.map((doc: { id: number; kind: string; number: string; version: number }) => (
                <li key={doc.id}>
                  <a href={api.documentUrl(doc.id)} target="_blank" rel="noreferrer">{words(doc.kind)} {doc.number}</a>
                  <span className="muted"> · version {doc.version}{c.status === 'reversed' ? ' · issued before reversal — kept as issued' : ''}</span>
                </li>
              ))}
            </ul>
          )}
          {d.memo ? <p>Delivery memo <strong>{d.memo.number ?? `#${d.memo.billId}`}</strong> — <Money paise={d.memo.totalPaise} /> <Pill>{d.memo.status}</Pill></p> : null}
          <h4 style={{ margin: '12px 0 6px' }}>What happened to it</h4>
          <ul className="timeline">
            {d.audit.map((a: { auditId: number; at: string; actorId: number; actorName: string | null; action: string; reason: string | null }) => (
              <li key={a.auditId}><strong>{actionWords(a.action)}</strong> by {a.actorName ?? `user ${a.actorId}`}{a.reason ? <> — {a.reason}</> : null}
                <div className="when">{when(a.at)}</div></li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}
